import { useState } from 'react';
import { useSignUp, useAuth } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import pricing from '../data/pricing';

const inputStyle = {
  width: '100%', backgroundColor: '#fff', border: '1px solid #D4CFC9',
  borderRadius: '6px', padding: '0.65rem 0.85rem', color: '#1A1A1A',
  fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none',
  fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', fontSize: '0.8rem', color: '#4A5568',
  marginBottom: '0.35rem', fontWeight: '500',
};

export default function Register() {
  const { isSignedIn } = useAuth();
  const { isLoaded, signUp } = useSignUp();

  const [step, setStep] = useState('form'); // 'form' | 'verify' | 'done'
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '',
    companyName: '', serviceTrack: 'managed_service', serviceTier: 'launchpad',
  });
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (isSignedIn) return <Navigate to="/" replace />;

  const tracks = Object.entries(pricing).map(([key, val]) => ({ key, label: val.label }));
  const tiers = Object.entries(pricing[form.serviceTrack]?.tiers || {}).map(([key, val]) => ({ key, ...val }));

  function setField(field, value) {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'serviceTrack') {
        next.serviceTier = Object.keys(pricing[value].tiers)[0];
      }
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isLoaded) return;
    setError('');
    setSubmitting(true);
    try {
      await signUp.create({
        firstName: form.firstName,
        lastName: form.lastName,
        emailAddress: form.email,
        password: form.password,
        unsafeMetadata: {
          company_name: form.companyName,
          service_track: form.serviceTrack,
          service_tier: form.serviceTier,
        },
      });

      if (signUp.status === 'complete') {
        setStep('done');
      } else {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        setStep('verify');
      }
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.errors?.[0]?.message || err.message || 'Something went wrong.');
    }
    setSubmitting(false);
  }

  async function handleVerify(e) {
    e.preventDefault();
    if (!isLoaded) return;
    setError('');
    setSubmitting(true);
    try {
      const result = await signUp.attemptEmailAddressVerification({ code });
      if (result.status === 'complete') {
        setStep('done');
      } else {
        setError('Verification incomplete. Please try again.');
      }
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.errors?.[0]?.message || 'Invalid code.');
    }
    setSubmitting(false);
  }

  const selectedTier = pricing[form.serviceTrack]?.tiers[form.serviceTier];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAF8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', padding: '2rem' }}>
      <div style={{ width: '100%', maxWidth: '480px' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src="/logo.png" alt="Client Engine" style={{ height: '48px', width: 'auto', marginBottom: '1.5rem' }} />
          {step === 'form' && (
            <>
              <h1 style={{ fontSize: '1.4rem', fontWeight: '600', color: '#1A1A1A', margin: '0 0 0.4rem' }}>Create your account</h1>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#888' }}>Get started with Client Engine today.</p>
            </>
          )}
          {step === 'verify' && (
            <>
              <h1 style={{ fontSize: '1.4rem', fontWeight: '600', color: '#1A1A1A', margin: '0 0 0.4rem' }}>Verify your email</h1>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#888' }}>We sent a 6-digit code to <strong>{form.email}</strong></p>
            </>
          )}
        </div>

        {step === 'done' && (
          <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '12px', padding: '2.5rem', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#F0FFF4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', fontSize: '1.4rem' }}>✓</div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A', margin: '0 0 0.75rem' }}>Thank you for signing up</h2>
            <p style={{ fontSize: '0.875rem', color: '#888', margin: '0 0 1.5rem', lineHeight: '1.6' }}>Your account is pending approval. We will be in touch shortly.</p>
            <p style={{ fontSize: '0.8rem', color: '#AAA', margin: 0 }}>Questions? Email us at <a href="mailto:info@webcite.ai" style={{ color: '#8B1A1A' }}>info@webcite.ai</a></p>
          </div>
        )}

        {step === 'verify' && (
          <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '12px', padding: '2rem' }}>
            <form onSubmit={handleVerify}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={labelStyle}>Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  style={{ ...inputStyle, textAlign: 'center', fontSize: '1.4rem', letterSpacing: '0.3em' }}
                  autoFocus
                />
              </div>
              {error && <p style={{ color: '#C53030', fontSize: '0.82rem', margin: '0 0 1rem' }}>{error}</p>}
              <button type="submit" disabled={submitting || code.length < 6}
                style={{ width: '100%', backgroundColor: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.75rem', fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer', opacity: submitting || code.length < 6 ? 0.6 : 1 }}>
                {submitting ? 'Verifying…' : 'Verify email'}
              </button>
            </form>
          </div>
        )}

        {step === 'form' && (
          <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '12px', padding: '2rem' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelStyle}>First name</label>
                  <input type="text" value={form.firstName} onChange={e => setField('firstName', e.target.value)} placeholder="Jane" required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Last name</label>
                  <input type="text" value={form.lastName} onChange={e => setField('lastName', e.target.value)} placeholder="Smith" required style={inputStyle} />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} placeholder="jane@company.com" required style={inputStyle} />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Password</label>
                <input type="password" value={form.password} onChange={e => setField('password', e.target.value)} placeholder="At least 8 characters" required minLength={8} style={inputStyle} />
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={labelStyle}>Company name</label>
                <input type="text" value={form.companyName} onChange={e => setField('companyName', e.target.value)} placeholder="Acme Ltd" required style={inputStyle} />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>How would you like to work with us?</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  {tracks.map(t => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setField('serviceTrack', t.key)}
                      style={{
                        padding: '0.65rem 0.75rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '500',
                        cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                        border: form.serviceTrack === t.key ? '2px solid #8B1A1A' : '1px solid #D4CFC9',
                        backgroundColor: form.serviceTrack === t.key ? '#FFF5F5' : '#fff',
                        color: form.serviceTrack === t.key ? '#8B1A1A' : '#4A5568',
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Service tier</label>
                <select value={form.serviceTier} onChange={e => setField('serviceTier', e.target.value)} style={inputStyle}>
                  {tiers.map(t => (
                    <option key={t.key} value={t.key}>{t.name} — {t.label}</option>
                  ))}
                </select>
                {selectedTier && (
                  <p style={{ margin: '0.4rem 0 0', fontSize: '0.78rem', color: '#888' }}>
                    {selectedTier.label} · billed monthly
                  </p>
                )}
              </div>

              {error && <p style={{ color: '#C53030', fontSize: '0.82rem', margin: '0 0 1rem' }}>{error}</p>}

              <button type="submit" disabled={submitting}
                style={{ width: '100%', backgroundColor: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.75rem', fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>
                {submitting ? 'Creating account…' : 'Create account'}
              </button>

              <p style={{ textAlign: 'center', margin: '1rem 0 0', fontSize: '0.82rem', color: '#888' }}>
                Already have an account?{' '}
                <a href="/" style={{ color: '#8B1A1A', textDecoration: 'none' }}>Sign in</a>
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
